import React from 'react'
import { Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type {
    ModelEvaluationPipelineSummary,
    ModelEvaluationTaskSummary,
} from '../../../types/modelManagement'

type EvaluationSchemeSide = 'baseline' | 'candidate'

interface ValidationAssetNameProps {
    name?: string | null
}

interface EvaluationPipelineSummaryProps {
    showTitle?: boolean
    side: EvaluationSchemeSide
    task: ModelEvaluationTaskSummary
}

const getPipeline = (
    task: ModelEvaluationTaskSummary,
    side: EvaluationSchemeSide,
): ModelEvaluationPipelineSummary | null => {
    const pipeline =
        side === 'baseline' ? task.baseline_pipeline : task.candidate_pipeline
    if (pipeline) return pipeline

    return {
        mosquito_version_id:
            side === 'baseline'
                ? task.baseline_mosquito_version_id
                : task.candidate_mosquito_version_id,
        mosquito_version_label:
            side === 'baseline'
                ? task.baseline_mosquito_version_label
                : task.candidate_mosquito_version_label,
        insect_version_id:
            side === 'baseline'
                ? task.baseline_insect_version_id
                : task.candidate_insect_version_id,
        insect_version_label:
            side === 'baseline'
                ? task.baseline_insect_version_label
                : task.candidate_insect_version_label,
    }
}

export const ValidationAssetName: React.FC<ValidationAssetNameProps> = ({
    name,
}) => <Typography.Text>{name || '-'}</Typography.Text>

export const EvaluationPipelineSummary: React.FC<
    EvaluationPipelineSummaryProps
> = ({ showTitle = true, side, task }) => {
    const { t } = useTranslation(['admin'])
    const pipeline = getPipeline(task, side)
    const mosquitoLabel = pipeline?.mosquito_version_label || '-'
    const insectLabel =
        pipeline?.insect_version_label ||
        t('admin:modelValidation.scheme.notSelected')

    return (
        <Space direction="vertical" size={0}>
            {showTitle ? (
                <Typography.Text strong>
                    {t(`admin:modelValidation.scheme.${side}`)}
                </Typography.Text>
            ) : null}
            <Typography.Text>
                {t('admin:modelValidation.scheme.mosquito')}: {mosquitoLabel}
            </Typography.Text>
            <Typography.Text>
                {t('admin:modelValidation.scheme.insect')}: {insectLabel}
            </Typography.Text>
        </Space>
    )
}

export const EvaluationTaskConfigurationSummary: React.FC<{
    variant?: 'compact' | 'report'
    task: ModelEvaluationTaskSummary
}> = ({ task, variant = 'compact' }) => {
    const { t } = useTranslation(['admin'])
    const datasetName =
        task.validation_dataset_name || task.validation_dataset_name_snapshot

    if (variant === 'report') {
        return (
            <div className="model-evaluation-report__configuration-grid">
                <div className="model-evaluation-report__configuration-item">
                    <Typography.Text type="secondary">
                        {t('admin:modelValidation.columns.dataset')}
                    </Typography.Text>
                    <Typography.Text strong>
                        <ValidationAssetName name={datasetName} />
                    </Typography.Text>
                </div>
                <div className="model-evaluation-report__configuration-item">
                    <EvaluationPipelineSummary side="baseline" task={task} />
                </div>
                <div className="model-evaluation-report__configuration-item">
                    <EvaluationPipelineSummary side="candidate" task={task} />
                </div>
            </div>
        )
    }

    return (
        <Space direction="vertical" size={6}>
            <Space size={6} wrap>
                <Typography.Text type="secondary">
                    {t('admin:modelValidation.columns.dataset')}:
                </Typography.Text>
                <ValidationAssetName name={datasetName} />
            </Space>
            <Space size="large" wrap>
                <EvaluationPipelineSummary side="baseline" task={task} />
                <EvaluationPipelineSummary side="candidate" task={task} />
            </Space>
        </Space>
    )
}
